<template>
  <v-menu :disabled="!item || !availableActions.length" location="bottom left">
    <template #activator="{ props: activatorProps }">
      <slot name="activator" :props="activatorProps">
        <v-btn data-testid="default-activator" icon="$dots" v-bind="activatorProps"></v-btn>
      </slot>
    </template>

    <v-list>
      <v-list-item v-for="action in availableActions" :key="action" @click="onActionClick(action)">
        <v-list-item-title :data-testid="`${actionTestIdPrefix}-${action}-action`">
          {{ $t(`manageWorkspaces.actions.${action}`) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, type PropType } from "vue";
import {
  MspWorkspaceAction,
  SubscriptionModule,
  SubscriptionType,
  WorkspaceType,
} from "@/constants/workplaces.ts";
import type { BaseMspWorkspaceListItem } from "@/_store/msp/msp.module.ts";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import type { WorkspaceActionsStore } from "@/_helpers/msp-actions.ts";
import { handleMspWorkspaceViewAction } from "@/_helpers/msp-actions.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import { type DialogDataConfig, useDialogsStore } from "@/_store/dialogs.module.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { ModalWidth } from "@/constants/modals";
import UpdateBillingSubscriptionModal from "@/components/modals/subscription/UpdateSubscriptionModal.vue";
import EditMspWorkspaceModal from "@/components/modals/msp/EditMspWorkspaceModal.vue";
import EditMspWorkspaceLabelsModal from "@/components/modals/msp/EditMspWorkspaceLabelsModal.vue";
import ConvertWorkspaceModal from "@/components/modals/msp/ConvertWorkspaceModal.vue";
import ExtendTrialModal from "@/components/modals/msp/ExtendTrialModal.vue";
import { i18n } from "@/plugins/i18n";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";

const PLANS_WITHOUT_SUBSCRIPTION = new Set([
  SubscriptionType.INITIAL,
  SubscriptionType.FREEZE,
  SubscriptionType.TRIAL,
]);

const PLANS_IN_TRIAL = new Set([SubscriptionType.FREEZE, SubscriptionType.TRIAL]);

export default defineComponent({
  props: {
    item: {
      type: Object as PropType<BaseMspWorkspaceListItem>,
      default: null,
    },
    itemActions: {
      type: Array as PropType<MspWorkspaceAction[]>,
      default: () => [],
    },
    context: {
      type: String as PropType<ManageWorkspacesContext>,
      required: true,
    },
    workspaceStore: {
      type: Object as PropType<WorkspaceActionsStore>,
      required: true,
    },
    disabledActionsMap: {
      type: Object as PropType<Record<MspWorkspaceAction, boolean>>,
      required: true,
    },
    actionTestIdPrefix: {
      type: String,
      default: "msp-actions-menu",
    },
  },
  setup(props) {
    const workspaceContextStore = useWorkspaceStore();
    const permissionsStore = usePermissionsStore();
    const dialogsStore = useDialogsStore();

    const disabledMap = computed(() => props.disabledActionsMap ?? {});

    const showAction = (action: MspWorkspaceAction, item: BaseMspWorkspaceListItem): boolean => {
      const isCurrentWorkspace =
        props.context === ManageWorkspacesContext.SINGLE_HIERARCHY &&
        item.workspaceId === workspaceContextStore.workspace.workspaceId;

      if (item.subscriptionType === SubscriptionType.ARCHIVED) {
        return action === MspWorkspaceAction.UNARCHIVE_WORKSPACE;
      }

      switch (action) {
        case MspWorkspaceAction.VIEW_WORKSPACE:
        case MspWorkspaceAction.EDIT_WORKSPACE:
          return true;
        case MspWorkspaceAction.EDIT_SUBSCRIPTION:
          return !isCurrentWorkspace;
        case MspWorkspaceAction.ARCHIVE_WORKSPACE:
          return item.canBeArchived;
        case MspWorkspaceAction.START_SUBSCRIPTION:
          return !isCurrentWorkspace && PLANS_WITHOUT_SUBSCRIPTION.has(item.subscriptionType);
        case MspWorkspaceAction.STOP_SUBSCRIPTION:
          return !isCurrentWorkspace && !PLANS_WITHOUT_SUBSCRIPTION.has(item.subscriptionType);
        case MspWorkspaceAction.CONVERT:
          return permissionsStore.isGlobalAdmin;
        case MspWorkspaceAction.EXTEND_TRIAL:
          return permissionsStore.isGlobalAdmin && PLANS_IN_TRIAL.has(item.subscriptionType);
        case MspWorkspaceAction.EDIT_MSP_WORKSPACE_LABEL:
          return item.type === WorkspaceType.CHILD;
        case MspWorkspaceAction.START_TRIAL:
          return (
            props.context === ManageWorkspacesContext.GLOBAL_WORKSPACES &&
            item.type === WorkspaceType.REGULAR &&
            !!item.trialAvailable
          );
        default:
          return false;
      }
    };

    const openEditLabelsDialog = async (item: BaseMspWorkspaceListItem, disabled: boolean) => {
      const workspaceDetails = await props.workspaceStore.getMspWorkspaceDetails(item.workspaceId);
      if (!workspaceDetails) return;

      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: MspWorkspaceAction.EDIT_MSP_WORKSPACE_LABEL,
          callback: props.workspaceStore.editWorkspace,
          component: EditMspWorkspaceLabelsModal,
          item: workspaceDetails,
          width: ModalWidth.SMALL,
          disable: disabled,
        }),
        scrollable: true,
      });
    };

    const openEditWorkspaceDialog = async (item: BaseMspWorkspaceListItem, disabled: boolean) => {
      const details = await props.workspaceStore.getMspWorkspaceDetails(item.workspaceId);
      if (!details) return;

      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: MspWorkspaceAction.EDIT_WORKSPACE,
          callback: props.workspaceStore.editWorkspace,
          component: EditMspWorkspaceModal,
          item: { ...details, context: props.context },
          width: ModalWidth.LARGE,
          disable: disabled,
        }),
        scrollable: true,
        header: {
          title: i18n.global.t(`modals.editWorkspace.titles.${item.type}`),
        },
      });
    };

    const openConvertDialog = (item: BaseMspWorkspaceListItem, disabled: boolean) => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: MspWorkspaceAction.CONVERT,
          callback: props.workspaceStore.convertWorkspace,
          component: ConvertWorkspaceModal,
          item,
          width: ModalWidth.LARGE,
          disable: disabled,
        }),
        scrollable: true,
      });
    };

    const openExtendTrialDialog = (item: BaseMspWorkspaceListItem, disabled: boolean) => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: MspWorkspaceAction.EXTEND_TRIAL,
          callback: props.workspaceStore.extendTrial,
          component: ExtendTrialModal,
          item,
          width: ModalWidth.LARGE,
          disable: disabled,
        }),
        scrollable: true,
      });
    };

    const openGenericConfirmationDialog = (
      action: MspWorkspaceAction,
      item: BaseMspWorkspaceListItem,
      disabled: boolean
    ) => {
      const callbackMap: Partial<
        Record<MspWorkspaceAction, (payload: DialogDataConfig) => Promise<unknown>>
      > = {
        [MspWorkspaceAction.START_SUBSCRIPTION]: props.workspaceStore.startSubscription,
        [MspWorkspaceAction.STOP_SUBSCRIPTION]: props.workspaceStore.stopSubscription,
        [MspWorkspaceAction.UNARCHIVE_WORKSPACE]: props.workspaceStore.unarchiveWorkspace,
        [MspWorkspaceAction.ARCHIVE_WORKSPACE]: props.workspaceStore.archiveWorkspace,
        // START_TRIAL is available only for the Global workspaces management page
        [MspWorkspaceAction.START_TRIAL]:
          "startTrial" in props.workspaceStore ? props.workspaceStore.startTrial : undefined,
      };

      const callback = callbackMap[action];
      if (!callback) return;

      const isSubscriptionToggle =
        action === MspWorkspaceAction.START_SUBSCRIPTION ||
        action === MspWorkspaceAction.STOP_SUBSCRIPTION;

      dialogsStore.openDialog(
        confirmationDialogsConfigConstructor({
          item: isSubscriptionToggle ? [item.workspaceId] : item,
          action,
          callback,
          disable: disabled,
        })
      );
    };

    const handleEditSubscriptionDialog = (
      action: MspWorkspaceAction,
      item: BaseMspWorkspaceListItem,
      disable: boolean
    ) => {
      const openSubscriptionConfirmationDialog = async (
        payload: BaseMspWorkspaceListItem & {
          modulesWhichRemovedDuringEdit: SubscriptionModule[];
        }
      ) => {
        dialogsStore.closeDialog();
        await nextTick();

        const removedModules = payload.modulesWhichRemovedDuringEdit
          .map((module) => i18n.global.t(`subscriptions.modules.${module}`))
          .join(", ");

        dialogsStore.openDialog(
          confirmationDialogsConfigConstructor({
            item: { ...payload, removedModules },
            action: MspWorkspaceAction.EDIT_SUBSCRIPTION_CONFIRMATION,
            disable: false,
            callback: async (confirmationPayload) => {
              const currentWorkspace = workspaceContextStore.workspace.workspaceId;
              await props.workspaceStore.editSubscription({ ...confirmationPayload.item });
              if (currentWorkspace === confirmationPayload.item.workspaceId) {
                await workspaceContextStore.updateWorkspaceAndPermissions(
                  confirmationPayload.item.workspaceId,
                  confirmationPayload.item.type !== WorkspaceType.REGULAR
                );
              }
            },
          })
        );
      };

      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action,
          callback: async (payload) => {
            const payloadItem = payload?.item;
            // Guard against empty payloads when the dialog hasn't emitted initial localValue.
            if (!payloadItem) {
              return;
            }

            // Default to empty so subscription edits without removals don't crash.
            const removedModules = payloadItem.modulesWhichRemovedDuringEdit ?? [];
            if (removedModules.length > 0) {
              await openSubscriptionConfirmationDialog({
                ...payloadItem,
                workspaceIds: [item.workspaceId],
              });
            } else {
              await props.workspaceStore.editSubscription({
                ...payloadItem,
                workspaceIds: [item.workspaceId],
              });
            }
          },
          component: UpdateBillingSubscriptionModal,
          item: {
            workspaceId: item.workspaceId,
            modules: item.subscription.modules,
            bundles: item.subscription.bundles,
          },
          width: ModalWidth.LARGE,
          disable,
        }),
        scrollable: true,
        fullscreen: true,
      });
    };

    const handleWorkspaceAction = async (
      { itemAction, item }: { itemAction: MspWorkspaceAction; item: BaseMspWorkspaceListItem },
      disabledActions: Record<MspWorkspaceAction, boolean>
    ) => {
      const disabled = disabledActions[itemAction] || false;

      switch (itemAction) {
        case MspWorkspaceAction.EDIT_MSP_WORKSPACE_LABEL:
          await openEditLabelsDialog(item, disabled);
          break;
        case MspWorkspaceAction.EDIT_WORKSPACE:
          await openEditWorkspaceDialog(item, disabled);
          break;
        case MspWorkspaceAction.EDIT_SUBSCRIPTION:
          handleEditSubscriptionDialog(itemAction, item, disabled);
          break;
        case MspWorkspaceAction.CONVERT:
          openConvertDialog(item, disabled);
          break;
        case MspWorkspaceAction.EXTEND_TRIAL:
          openExtendTrialDialog(item, disabled);
          break;
        case MspWorkspaceAction.UNARCHIVE_WORKSPACE:
        case MspWorkspaceAction.START_SUBSCRIPTION:
        case MspWorkspaceAction.STOP_SUBSCRIPTION:
        case MspWorkspaceAction.ARCHIVE_WORKSPACE:
        case MspWorkspaceAction.START_TRIAL:
          openGenericConfirmationDialog(itemAction, item, disabled);
          break;
        case MspWorkspaceAction.VIEW_WORKSPACE:
        case MspWorkspaceAction.VIEW_PROTECTED_USERS:
        case MspWorkspaceAction.VIEW_DEVICES:
        case MspWorkspaceAction.VIEW_TICKETS:
          await handleMspWorkspaceViewAction(itemAction, item);
          break;
      }
      const gtmPayload = `${GTMParams.WORKSPACE_ACTIONS_CLICK} - ${startCase(itemAction)}`;
      trackGtmEvent(GtmAction.MSP_WORKSPACE_PAGE, gtmPayload);
    };

    const handleItemAction = async (action: MspWorkspaceAction, item: BaseMspWorkspaceListItem) => {
      await handleWorkspaceAction(
        {
          itemAction: action,
          item,
        },
        disabledMap.value
      );
    };

    const availableActions = computed(() => {
      if (!props.item) {
        return [];
      }

      return props.itemActions.filter((action) => showAction(action, props.item!));
    });

    const onActionClick = (action: MspWorkspaceAction) => {
      if (props.item) {
        handleItemAction(action, props.item);
      }
    };

    return {
      availableActions,
      onActionClick,
      handleItemAction,
      handleWorkspaceAction,
      showAction,
    };
  },
});
</script>
